import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';
import { FINANCE } from '../../theme/financeTheme';

const useStyles = makeStyles({
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    margin: '2px 8px',
    cursor: 'pointer',
    color: FINANCE.gray,
    backgroundColor: 'transparent',
    borderRadius: FINANCE.radiusSm,
    borderLeft: '3px solid transparent',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: FINANCE.wash,
      color: FINANCE.text,
    },
  },
  itemActive: {
    color: FINANCE.primaryDark,
    backgroundColor: FINANCE.navActiveBg,
    borderLeftColor: FINANCE.primary,
    fontWeight: 600,
    '& $icon': {
      color: FINANCE.primary,
    },
  },
  // Parent dropdown header: a group toggle, NOT a selectable value.
  // Kept visually lighter than a selected value so the two never look alike.
  groupHeader: {
    color: FINANCE.text,
    fontWeight: 600,
  },
  // Group header when one of its children is the current view: emphasise it
  // as the open section, but without the full "selected value" pill.
  groupHeaderOpen: {
    color: FINANCE.primaryDark,
    backgroundColor: FINANCE.wash,
    '& $icon': {
      color: FINANCE.primary,
    },
  },
  subItem: {
    position: 'relative',
    paddingLeft: 30,
    marginLeft: 8,
    marginRight: 8,
    color: FINANCE.muted,
    // vertical rail that visually ties children to their dropdown parent
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 16,
      top: 4,
      bottom: 4,
      width: 2,
      borderRadius: 2,
      backgroundColor: FINANCE.line,
    },
    '&:hover': {
      backgroundColor: 'transparent',
      color: FINANCE.text,
    },
  },
  // The selected value inside the dropdown: highlighted rail segment + darker,
  // bolder text — no filled background pill.
  subItemActive: {
    color: FINANCE.text,
    backgroundColor: 'transparent',
    fontWeight: 600,
    // highlight the rail segment beside the selected value
    '&::before': {
      backgroundColor: FINANCE.primary,
    },
  },
  icon: {
    fontSize: 20,
    color: FINANCE.muted,
    transition: 'color 0.15s ease',
  },
  text: {
    fontSize: '0.8125rem',
    fontWeight: 'inherit',
    flexGrow: 1,
  },
  chevron: {
    fontSize: 18,
    color: FINANCE.muted,
    transition: 'transform 0.2s ease',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
    color: FINANCE.primary,
  },
});

interface FinanceNavItemProps {
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  text: string;
  to: string;
  matchPaths?: string[];
  isSubItem?: boolean;
  /** Render as an expandable dropdown header with a chevron indicator. */
  expandable?: boolean;
  /** Whether the dropdown is currently open (controls chevron direction). */
  expanded?: boolean;
  /** Toggle handler for the dropdown chevron. */
  onToggleExpand?: () => void;
}

export const FinanceNavItem = ({
  icon: Icon,
  text,
  to,
  matchPaths,
  isSubItem,
  expandable,
  expanded,
  onToggleExpand,
}: FinanceNavItemProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const paths = matchPaths ?? [to];
  const current = `${location.pathname}${location.search}`;

  const isActive = paths.some(p => {
    const full = p.startsWith('/') ? p : `/${p}`;
    if (full.includes('?')) {
      if (current === full) return true;
      const fullParams = new URLSearchParams(full.split('?')[1] ?? '');
      const curParams = new URLSearchParams(location.search);
      const pathView = fullParams.get('view');
      const currentView = curParams.get('view');
      if (!pathView || pathView !== currentView) return false;
      // When the match path pins a specific forecast sub-tab, the current tab
      // must match it exactly (so sub-nav items don't all light up at once).
      const pathTab = fullParams.get('tab');
      if (pathTab) {
        return (curParams.get('tab') ?? 'client-summary') === pathTab;
      }
      return true;
    }
    if (full === '/finance/reporting') {
      const params = new URLSearchParams(location.search);
      const v = params.get('view');
      return (
        location.pathname === '/finance/reporting' && (!v || v === 'pl')
      );
    }
    return location.pathname === full || location.pathname.startsWith(`${full}/`);
  });

  // Choose the active-state class based on the role of the row so a dropdown
  // header and a selected value never share the same treatment.
  const activeClass = isActive
    ? expandable
      ? classes.groupHeaderOpen
      : isSubItem
      ? classes.subItemActive
      : classes.itemActive
    : '';

  const className = [
    classes.item,
    expandable ? classes.groupHeader : '',
    isSubItem ? classes.subItem : '',
    activeClass,
  ]
    .filter(Boolean)
    .join(' ');

  const handleChevronClick = (e: React.MouseEvent) => {
    if (!onToggleExpand) return;
    e.stopPropagation();
    // Expanding a dropdown should also select (navigate to) it, so the user
    // lands on the section rather than just revealing its children while
    // still viewing the previous page. Collapsing leaves them in place.
    if (!expanded) {
      navigate(`/${to}`);
    }
    onToggleExpand();
  };

  return (
    <Box
      className={className}
      onClick={() => navigate(`/${to}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && navigate(`/${to}`)}
      aria-expanded={expandable ? Boolean(expanded) : undefined}
    >
      {Icon && <Icon className={classes.icon} />}
      <Typography className={classes.text}>{text}</Typography>
      {expandable && (
        <ExpandMoreIcon
          className={`${classes.chevron} ${expanded ? classes.chevronOpen : ''}`}
          onClick={handleChevronClick}
          aria-hidden
        />
      )}
    </Box>
  );
};
